import { OperationApplier } from '@common/prosemirror/operations/operation-applier.type';
import { parseOperations } from '@common/prosemirror/operations/operations';
import { Plugin, PluginKey } from 'prosemirror-state';

export const operationPluginKey = new PluginKey('operation');

/*
 * A generic ProseMirror plugin for processing the document operations.
 * It parses the operations from transaction steps and uses the specified OperationApplier to validate each operation.
 * If all parsed operations are valid then it will be allowed to the specified OperationApplier to apply them and return the new plugin state.
 */
export function operationPlugin<TPluginState>(operationApplier: OperationApplier<TPluginState>): Plugin {

  return new Plugin({
    key: operationPluginKey,

    state: {
      init(_, { doc }) {
        return operationApplier.createDataStore(doc);
      },

      apply(tr, pluginState, oldState, newState) {
        // parse operations from steps
        const operations = parseOperations(tr.steps);
        // if operations are found
        if (operations) {
          let currentPluginState = pluginState;
          // apply each operation
          operations.forEach(operation => {
            const docBefore = tr.docs[operation.firstInnerStepIndex];
            const docAfter = tr.docs[operation.lastInnerStepIndex + 1];
            const steps = tr.steps.slice(operation.firstInnerStepIndex, operation.lastInnerStepIndex + 1);
            currentPluginState = operationApplier.applyOperation(operation.operationName, steps, currentPluginState, docBefore, docAfter, tr.getMeta(operationPluginKey));
          });
          // return the new plugin state
          return currentPluginState;
        }
        // else create the plugin state for unknown changes
        else if (tr.docChanged) {
          return operationApplier.createDataStore(newState.doc);
        }
        // else return the old plugin state
        return pluginState;
      },
    },

    filterTransaction(tr, state) {
      const meta = tr.getMeta(operationPluginKey);
      // if the current transaction marked as a transaction with confirmed operations
      if (meta?.hasConfirmedOperations) {
        // we can skip validation
        return true;
      }

      let pluginState = operationPluginKey.getState(state);
      // parse operations from steps
      const operations = parseOperations(tr.steps);
      // if operations are found
      if (operations) {
        // validate each operation
        const allValid = operations.every((operation, index) => {
          const isLast = index === operations.length - 1;
          const docBefore = tr.docs[operation.firstInnerStepIndex];
          const docAfter = tr.docs[operation.lastInnerStepIndex + 1];
          const steps = tr.steps.slice(operation.firstInnerStepIndex, operation.lastInnerStepIndex + 1);
          // if operation is valid
          if (operationApplier.validateOperation(operation.operationName, steps, pluginState, docBefore, docAfter)) {
            // if it is not the last operation then apply it to the plugin state
            if (!isLast) {
              pluginState = operationApplier.applyOperation(operation.operationName, steps, pluginState, docBefore, docAfter, tr.getMeta(operationPluginKey));
            }
            return true;
          } else {
            return false;
          }
        });
        // return the result
        return allValid;
      }

      // consider the transaction without operations as valid
      return true;
    },
  });

}
